// Jurisdictional compliance assessment — how the document's language meshes
// with each jurisdiction on the panel (NV, DE, TX, CA, NY, Cayman, EU, US…).
// Runs the AI screen via /legal/jurisdiction, keeps history under
// docs/{id}/assessments, and deep-links every finding to its clause.
// AI-generated screening aid for counsel — clearly labeled, never legal advice.
import React, { useMemo, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { useToast } from '../components/Toast';
import { useOrg } from '../data/orgs';
import { useEntityDoc, useEntityList } from '../data/entities';
import { aiJurisdiction, aiDocAgent, isAiConfigured } from '../data/legalApi';
import { useDealPowers } from '../data/permissions';

const DEFAULT_JURISDICTIONS = [
  'Nevada', 'Delaware', 'Texas', 'California', 'New York', 'Minnesota',
  'Illinois', 'Washington', 'Colorado',
  'Cayman Islands', 'European Union', 'United States (federal)',
];

const STATUS_TONE = { ok: 'b-ok', caution: 'b-warn', conflict: 'b-danger' };
const STATUS_LABEL = { ok: 'No issues flagged', caution: 'Caution', conflict: 'Conflict' };
const SEVERITY_COLOR = { low: 'var(--info)', medium: 'var(--warn)', high: 'var(--danger)' };

function JurisdictionCard({ j, docBase }) {
  const [open, setOpen] = useState(j.status !== 'ok');
  return (
    <div className="cs-card u-stack" style={{ '--gap': 'var(--sp-3)' }}>
      <div className="u-split" style={{ alignItems: 'center' }}>
        <strong>{j.jurisdiction}</strong>
        <span className="u-cluster" style={{ '--gap': 'var(--sp-2)', flexShrink: 0 }}>
          {j.findings.length > 0 && (
            <button className="cs-btn cs-btn-ghost cs-btn-sm" onClick={() => setOpen((o) => !o)} aria-expanded={open}>
              {j.findings.length} finding{j.findings.length === 1 ? '' : 's'}
            </button>
          )}
          <span className={`cs-badge ${STATUS_TONE[j.status] || ''}`}>{STATUS_LABEL[j.status] || j.status}</span>
        </span>
      </div>
      {j.summary && <p className="u-small u-muted" style={{ margin: 0 }}>{j.summary}</p>}
      {open && j.findings.length > 0 && (
        <div className="u-stack" style={{ '--gap': 'var(--sp-2)' }}>
          {j.findings.map((f, i) => (
            <div key={i} className="u-stack u-small" style={{ '--gap': '4px', borderLeft: `3px solid ${SEVERITY_COLOR[f.severity] || 'var(--warn)'}`, paddingLeft: 'var(--sp-3)' }}>
              <span className="u-cluster" style={{ '--gap': 'var(--sp-2)', alignItems: 'center' }}>
                <span className="cs-chip" style={{ borderColor: SEVERITY_COLOR[f.severity], color: SEVERITY_COLOR[f.severity] }}>{f.severity}</span>
                <strong>{f.topic || 'Finding'}</strong>
                {f.clauseId ? (
                  <Link className="u-xs" to={`${docBase}?clause=${f.clauseId}`}>{f.clauseHeading}</Link>
                ) : (
                  <span className="u-xs u-muted">{f.clauseHeading}</span>
                )}
              </span>
              <span>{f.issue}</span>
              {f.suggestion && <span className="u-muted"><strong>Suggestion:</strong> {f.suggestion}</span>}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

export default function Compliance() {
  const { dealId, docId } = useParams();
  const { orgId } = useOrg();
  const toast = useToast();
  const navigate = useNavigate();
  const { can } = useDealPowers(dealId);
  const scope = orgId ? { org: orgId, deal: dealId } : null;

  const { doc: deal } = useEntityDoc('deal', orgId ? { org: orgId } : null, orgId ? dealId : null);
  const { doc } = useEntityDoc('dealDoc', scope, scope ? docId : null);
  const { docs: assessments } = useEntityList(
    'assessment',
    scope ? { ...scope, dealDoc: docId } : null,
    { enabled: !!scope }
  );

  const [selected, setSelected] = useState(() => new Set(DEFAULT_JURISDICTIONS));
  const [customName, setCustomName] = useState('');
  const [viewingId, setViewingId] = useState(null);
  const [running, setRunning] = useState(false);
  const [proposing, setProposing] = useState(false);
  const extraJuris = [...selected].filter((n) => !DEFAULT_JURISDICTIONS.includes(n));

  const sorted = useMemo(
    () => [...(assessments || [])].sort((a, b) => b.createdAt - a.createdAt),
    [assessments]
  );
  const assessment = sorted.find((a) => a.id === viewingId) || sorted[0] || null;

  const toggle = (name) =>
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(name)) next.delete(name);
      else next.add(name);
      return next;
    });

  const addCustom = (e) => {
    e?.preventDefault();
    const name = customName.trim();
    if (!name) return;
    setSelected((prev) => new Set(prev).add(name));
    setCustomName('');
  };

  const proposeFixes = async () => {
    if (!assessment || !can('agent')) return;
    const lines = (assessment.jurisdictions || []).flatMap((j) =>
      (j.findings || [])
        .filter((f) => f.severity !== 'low')
        .map((f) => `${j.jurisdiction} · ${f.severity} · ${f.clauseHeading || 'clause'}: ${f.issue}${f.suggestion ? ` → ${f.suggestion}` : ''}`)
    );
    if (!lines.length) {
      toast('No medium/high findings to incorporate', 'warn');
      return;
    }
    setProposing(true);
    try {
      const r = await aiDocAgent({
        orgId,
        dealId,
        docId,
        mode: 'precision',
        instruction:
          `Apply this jurisdiction screen to the draft. Edit the named sections so the language follows each suggestion. ` +
          `This is a statute/rule compliance edit — do not invent what other companies typically accept or pay.\n\n${lines.join('\n')}`,
      });
      if (r.counsel) {
        toast('The agent answered instead of editing — try Instruct from the project page', 'warn');
        return;
      }
      if (r.misplaced) {
        toast(r.summary || 'Belongs in another document', 'warn');
        return;
      }
      toast(`Proposal filed — ${(r.ops || []).length} change${(r.ops || []).length === 1 ? '' : 's'} to review`, 'ok');
      navigate(`/deals/${dealId}/docs/${docId}`);
    } catch (err) {
      toast(err.message, 'danger');
    } finally {
      setProposing(false);
    }
  };

  const run = async () => {
    if (!selected.size) return;
    setRunning(true);
    try {
      const r = await aiJurisdiction({ orgId, dealId, docId, jurisdictions: [...selected] });
      setViewingId(r.assessmentId);
      toast(`Assessment complete across ${r.jurisdictions.length} jurisdictions`, 'ok');
    } catch (err) {
      toast(err.message, 'danger');
    } finally {
      setRunning(false);
    }
  };

  if (!orgId || doc === undefined || !deal) {
    return <div className="cs-page"><div className="cs-skeleton" style={{ height: 200 }} /></div>;
  }
  if (doc === null) {
    return <div className="cs-page"><div className="cs-banner b-danger">Document not found.</div></div>;
  }

  const docBase = `/deals/${dealId}/docs/${docId}`;
  const stale = assessment && doc.currentVersionId && assessment.versionId !== doc.currentVersionId;

  return (
    <div className="cs-page u-stack m-rise" style={{ '--gap': 'var(--sp-5)' }}>
      <div className="u-small u-muted">
        <Link to="/projects">Projects</Link> / <Link to={`/deals/${dealId}`}>{deal.name}</Link> /{' '}
        <Link to={docBase}>{doc.title}</Link> / Jurisdictions
      </div>

      <div className="cs-page-head">
        <div>
          <h1>Jurisdictional assessment</h1>
          <p className="u-muted u-small">
            Statute/rule screen of “{doc.title}” — not a market survey, not legal advice. Name Minnesota (or anywhere) and propose the findings into the draft.
          </p>
        </div>
      </div>

      <div className="cs-card u-stack" style={{ '--gap': 'var(--sp-3)' }}>
        <strong className="u-small">Jurisdiction panel</strong>
        <div className="u-cluster" style={{ '--gap': 'var(--sp-2)' }}>
          {[...DEFAULT_JURISDICTIONS, ...extraJuris].map((name) => (
            <button
              key={name}
              type="button"
              className={`cs-btn cs-btn-sm ${selected.has(name) ? 'cs-btn-primary' : 'cs-btn-outline'}`}
              onClick={() => toggle(name)}
              aria-pressed={selected.has(name)}
            >
              {name}
            </button>
          ))}
        </div>
        <form className="u-cluster" style={{ '--gap': 'var(--sp-2)' }} onSubmit={addCustom}>
          <input
            className="cs-input"
            style={{ maxWidth: 240 }}
            value={customName}
            onChange={(e) => setCustomName(e.target.value)}
            placeholder="Add a jurisdiction — e.g. Minnesota"
            aria-label="Add a jurisdiction"
          />
          <button type="submit" className="cs-btn cs-btn-outline cs-btn-sm" disabled={!customName.trim()}>Add</button>
        </form>
        <div className="u-cluster" style={{ '--gap': 'var(--sp-3)', alignItems: 'center' }}>
          <button className="cs-btn cs-btn-primary" disabled={running || !selected.size || !isAiConfigured()} onClick={run}>
            {running ? 'Assessing… (can take ~1 min)' : 'Run assessment'}
          </button>
          {assessment && can('agent') && (
            <button
              type="button"
              className="cs-btn cs-btn-outline"
              disabled={proposing || running || !isAiConfigured()}
              onClick={proposeFixes}
              title="File a reviewable edit that incorporates medium and high findings"
            >
              {proposing ? 'Proposing…' : 'Propose these fixes'}
            </button>
          )}
          {sorted.length > 0 && (
            <>
              <label className="u-small u-muted" htmlFor="a-history">History</label>
              <select
                id="a-history"
                className="cs-input"
                value={assessment?.id || ''}
                onChange={(e) => setViewingId(e.target.value)}
              >
                {sorted.map((a) => (
                  <option key={a.id} value={a.id}>
                    {new Date(a.createdAt).toLocaleString()} — v{a.versionNumber} · {a.jurisdictions?.length || 0} jurisdictions
                  </option>
                ))}
              </select>
            </>
          )}
        </div>
      </div>

      {stale && (
        <div className="cs-banner b-warn" role="status">
          This assessment covers v{assessment.versionNumber}, but the draft has moved on — re-run for the current version.
        </div>
      )}

      {!assessment && !running && (
        <div className="cs-banner b-info">
          No assessment yet. Pick the jurisdictions that matter to this deal and run the screen — findings link straight to the affected clause.
        </div>
      )}

      {assessment && (
        <>
          <div className="u-split">
            <span className="u-small u-muted">
              Assessed v{assessment.versionNumber} · {new Date(assessment.createdAt).toLocaleString()} · requested by {assessment.requestedByName}
            </span>
            <span className="u-cluster" style={{ '--gap': 'var(--sp-2)' }}>
              {['conflict', 'caution', 'ok'].map((s) => {
                const n = (assessment.jurisdictions || []).filter((j) => j.status === s).length;
                return n ? <span key={s} className={`cs-badge ${STATUS_TONE[s]}`}>{n} {s}</span> : null;
              })}
            </span>
          </div>
          <div className="u-stack" style={{ '--gap': 'var(--sp-3)' }}>
            {(assessment.jurisdictions || []).map((j) => (
              <JurisdictionCard key={j.jurisdiction} j={j} docBase={docBase} />
            ))}
          </div>
          <p className="u-xs u-muted" style={{ margin: 0 }}>
            AI-generated screening — verify with qualified counsel in each jurisdiction before relying on any finding.
          </p>
        </>
      )}
    </div>
  );
}
